import { useRef, useState } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'defaultValue'> & {
  value: string;
  /** Called on Enter / blur when the text changed. Return false to reject (reverts). */
  onCommit(value: string): boolean | void;
  multiline?: false;
};

/**
 * Text field that edits a local draft and commits on Enter or blur (one undo step per edit);
 * Escape reverts. Re-keyed by its value from the parent so remote changes reset the draft.
 */
function CommitInputInner({ value, onCommit, onKeyDown, onBlur, ...rest }: Props) {
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    if (draft === value) return;
    if (onCommit(draft) === false) setDraft(value);
  };
  return (
    <input
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          e.preventDefault();
        } else if (e.key === 'Escape') {
          setDraft(value);
          cancelled.current = true;
          e.currentTarget.blur();
          e.stopPropagation();
        }
        onKeyDown?.(e);
      }}
    />
  );
}

export function CommitInput(props: Props) {
  return <CommitInputInner key={props.value} {...props} />;
}

export function CommitTextarea({
  value,
  onCommit,
  ...rest
}: {
  value: string;
  onCommit(v: string): void;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>) {
  return <TextareaInner key={value} value={value} onCommit={onCommit} {...rest} />;
}

function TextareaInner({
  value,
  onCommit,
  ...rest
}: { value: string; onCommit(v: string): void } & Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
>) {
  const [draft, setDraft] = useState(value);
  return (
    <textarea
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== value && onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          if (draft !== value) onCommit(draft);
        } else if (e.key === 'Escape') {
          setDraft(value);
          e.stopPropagation();
        }
      }}
    />
  );
}
